/**
 * Toast shim — adapts the admin's existing `notify` / `notifyProblem` API
 * surface onto the unified `@wmart/ui` ToastProvider/useToast (P11-T10).
 *
 * Rationale:
 *  - The admin app shipped in Plan 08 with its own `lib/toast.tsx` exposing
 *    `useToast(): { notify(kind, message), notifyProblem(problem) }`.
 *  - `@wmart/ui` ships a richer ToastProvider with a queued bottom-right
 *    portal and a `useToast(): { push({title, body, tone}), dismiss(id) }`
 *    contract.
 *  - P11-T10 spec recommends "option (b)" — keep this file as a thin
 *    re-export/shim so the 23 existing call sites need ZERO churn while
 *    the underlying provider is the unified one. Modal refactors (T11)
 *    will continue to use the same `useToast()` hook surface; new code
 *    can also call `useToast` from `@wmart/ui` directly for the queued
 *    push/dismiss API.
 *
 * Toast tone mapping:
 *  - "success" → tone "success" with `title = "Saved"` (the original
 *    impl used the message as the only visible content; we put it in
 *    the body so the toast feels editorial — title is the kind, body
 *    is the message).
 *  - "error"   → tone "danger".
 *
 * notifyProblem uses `summariseProblem(problem)` (kept from the original
 * impl) and routes through `notify("error", ...)` so RFC 7807 wiring is
 * preserved verbatim.
 */
import { useMemo, type ReactNode } from "react";
import {
  ToastProvider as UIToastProvider,
  useToast as useUIToast,
} from "@wmart/ui";
import { summariseProblem, type Problem } from "./problem.js";

export interface ToastApi {
  notify: (kind: "success" | "error", message: string) => void;
  notifyProblem: (p: Problem) => void;
}

/**
 * ToastProvider re-export — delegates entirely to `@wmart/ui`. App.tsx
 * mounts this at the root; all child useToast() calls (admin shim or
 * direct `@wmart/ui` import) read from the same provider.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  return <UIToastProvider>{children}</UIToastProvider>;
}

/**
 * useToast — admin-shim that adapts the @wmart/ui contract to the
 * existing { notify, notifyProblem } surface. Memoised so consumers can
 * include `{notify, notifyProblem}` in `useEffect` deps without churning
 * on every render.
 */
export function useToast(): ToastApi {
  const { push } = useUIToast();
  return useMemo<ToastApi>(
    () => ({
      notify(kind, message) {
        push({
          tone: kind === "success" ? "success" : "danger",
          title: kind === "success" ? "Saved" : "Error",
          body: message,
        });
      },
      notifyProblem(p) {
        push({
          tone: "danger",
          title: "Error",
          body: summariseProblem(p),
        });
      },
    }),
    [push],
  );
}
